/**
 * `.rl-detail-name` and the input that replaces it — the reel's name, edited
 * where it is read (CAE-1552).
 *
 * Enter commits, Escape cancels, and blur commits too, which is the handoff's
 * own rule. Blur-commits is the unusual half: a field an operator clicks away
 * from usually *keeps* what was typed rather than writing it. It is kept because
 * there is no Save beside the input to click away *to* — the one in the header
 * saves the contest form, not this — so a blur that discarded would throw the
 * edit away with no way to have avoided it.
 *
 * **An unchanged name writes nothing.** The old panel disabled its Save button
 * when the draft matched what was stored; the same guard is here, where it has
 * to be, because blur fires whether or not anything was typed — without it,
 * opening the pencil and clicking away would PUT the reel and flash "Reel name
 * saved" at an operator who changed nothing.
 */
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import type { Theme } from '@mui/material/styles';
import { EditIcon } from '../../../../components/ui';
import type { ReelSlot } from './reel-slots';

export interface ReelNameProps {
  slot: ReelSlot;
  editable: boolean;
  /** `''` clears the override and puts the reel back to `Reel N`. */
  onRename: (label: string) => void;
}

export const ReelName = ({ slot, editable, onRename }: ReelNameProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');
  // Set by Escape so the blur that follows it does not commit what Escape has
  // just thrown away — the input is removed from the DOM, and a removal fires
  // blur like any other.
  const cancelled = useRef(false);

  // The pencil closes when the rail moves: the input would otherwise be left
  // open over a different reel's name, ready to rename the wrong one.
  useEffect(() => setIsEditing(false), [slot.reelNumber]);

  const commit = () => {
    if (cancelled.current) return;
    setIsEditing(false);
    const stored = slot.reel?.label ?? '';
    if (draft.trim() === stored.trim()) return;
    onRename(draft);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commit();
    }
    if (event.key === 'Escape') {
      // Consumed here: the step sits inside no modal, but the Popovers on the
      // rows below listen on `document` in the capture phase, and an Escape
      // that reached them would shut a panel the operator is not looking at.
      event.stopPropagation();
      cancelled.current = true;
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <Box
        component="input"
        autoFocus
        aria-label={`Reel name — ${slot.title}`}
        value={draft}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setDraft(event.target.value)}
        onKeyDown={onKeyDown}
        onBlur={commit}
        placeholder={`Reel ${slot.reelNumber}`}
        sx={(theme: Theme) => ({
          font: 'inherit',
          fontSize: '22px',
          fontWeight: 700,
          letterSpacing: '-0.015em',
          color: theme.tokens.color.ink,
          border: `${theme.tokens.border.width} solid ${theme.tokens.color.accent}`,
          borderRadius: `${theme.tokens.radius.control}px`,
          padding: '4px 10px',
          margin: '0 0 10px',
          outline: 'none',
          width: '320px',
          maxWidth: '100%',
          backgroundColor: theme.tokens.color.surface,
          boxShadow: theme.tokens.shadow.currentStepRing,
        })}
      />
    );
  }

  return (
    /*
     * The pencil is a sibling of the heading rather than a child of it. Inside,
     * its label joins the heading's own accessible name — "Reel 1 Rename Reel
     * 1" — and the heading stops being findable by the reel it names.
     */
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
      <Box
        component="h2"
        sx={(theme: Theme) => ({
          margin: 0,
          fontSize: '22px',
          fontWeight: 700,
          letterSpacing: '-0.015em',
          lineHeight: 1.2,
          color: theme.tokens.color.ink,
        })}
      >
        {slot.title}
      </Box>
      <Tooltip title="Rename reel">
        <span>
          <IconButton
            size="small"
            disabled={!editable}
            aria-label={`Rename ${slot.title}`}
            onClick={() => {
              cancelled.current = false;
              setDraft(slot.reel?.label ?? '');
              setIsEditing(true);
            }}
            sx={(theme: Theme) => ({ color: theme.tokens.color.ink4 })}
          >
            <EditIcon />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};
